import { FONT_FORMATS, type FontFormat, MAX_FONT_UPLOAD_BYTES } from '@repo/api-contracts';

export type FontFileCheck =
  | { readonly ok: true; readonly format: FontFormat }
  | { readonly ok: false; readonly message: string };

interface FontFileLike {
  readonly name: string;
  readonly size: number;
}

/** 마지막 확장자(점 뒤) — 없으면 null. */
const EXTENSION = /\.([^.]+)$/;

const isFontFormat = (value: string): value is FontFormat =>
  (FONT_FORMATS as readonly string[]).includes(value);

/**
 * 폰트 파일의 형식을 확장자로 정하고 크기를 계약 상한으로 거른다 — 서버 create 도 같은 경계로 다시 본다.
 * file.type 은 보지 않는다. 폰트의 MIME 은 브라우저·OS 마다 빈 값이거나 application/octet-stream 이라,
 * 형식도 PUT content-type(FONT_CONTENT_TYPES[형식])도 확장자에서 나온다.
 */
export const checkFontFile = (file: FontFileLike): FontFileCheck => {
  const extension = EXTENSION.exec(file.name)?.[1]?.toLowerCase();

  if (extension === undefined || !isFontFormat(extension)) {
    return { ok: false, message: 'woff2·ttf·otf 형식의 폰트 파일만 올릴 수 있습니다.' };
  }

  if (file.size <= 0) {
    return { ok: false, message: '빈 파일은 올릴 수 없습니다.' };
  }

  if (file.size > MAX_FONT_UPLOAD_BYTES) {
    return {
      ok: false,
      message: '폰트 파일은 10MB 이하여야 합니다. 한글 전체 글꼴은 woff2 로 올려 주세요.',
    };
  }

  return { ok: true, format: extension };
};

/** 이름 칸을 미리 채울 기본 폰트 이름 — 파일 이름에서 마지막 확장자를 뗀다. */
export const familyNameFromFileName = (fileName: string): string => fileName.replace(EXTENSION, '');
